import { Space } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import StickyPageHeader from '../components/common/StickyPageHeader';
import RatesFilter from '../components/filters/RatesFilter';
import RateTable from '../components/tables/RateTable';
import { useCarriers, useDefaultModes, useShipTypes } from '../hooks/apiHooks';
import { useRates } from '../hooks/apiHooks/rate';
import Layout from '../layout';
import { isLocationMatch } from '../models/Rate';

const AdminRatesPage = ({ disableLayout, routes }) => {
  const { allCarriers, isLoading: isCarriersLoading } = useCarriers();
  const { allShipTypes } = useShipTypes();
  const { defaultModes } = useDefaultModes();
  const { isLoading: isRatesLoading, rates } = useRates();
  const [filter, setFilter] = useState(null);
  const [highlightRateId, setHighlightRateId] = useState(null);

  useEffect(() => {
    if (highlightRateId) {
      const timer = setTimeout(() => setHighlightRateId(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [highlightRateId]);

  const handleClearFiltersAndGoToRate = useCallback((rateId) => {
    setFilter(null);
    setHighlightRateId(rateId);
  }, []);

  const filteredRates = useMemo(() => {
    if (!filter) return [];
    const activeCarrierIds = new Set(allCarriers.map((c) => c.id));
    // Check if it's a unified search
    const isUnifiedSearch = filter.searchType !== 'separate';
    const filtered = rates.filter(
      (rate) =>
        activeCarrierIds.has(rate.carrierId) &&
        (!filter.carrierIds || filter.carrierIds.length === 0 || filter.carrierIds.includes(rate.carrierId)) &&
        (!filter.modeNames || filter.modeNames.length === 0 || filter.modeNames.includes(rate.modeName)) &&
        (!filter.origin ||
          isLocationMatch(rate, filter.origin, true, isUnifiedSearch)) &&
        (!filter.destination ||
          isLocationMatch(rate, filter.destination, false, false))
    );

    // sort exact matches first
    if (filter.origin || filter.destination) {
      const originText = filter.origin?.trim().replace(/^["']|["']$/g, '').toLowerCase() || '';
      const destText = filter.destination?.trim().replace(/^["']|["']$/g, '').toLowerCase() || '';

      return filtered.sort((a, b) => {
        const aExact = (originText && a.origin.toLowerCase().includes(originText)) ||
          (destText && a.destination.toLowerCase().includes(destText));
        const bExact = (originText && b.origin.toLowerCase().includes(originText)) ||
          (destText && b.destination.toLowerCase().includes(destText));
        return aExact && !bExact ? -1 : !aExact && bExact ? 1 : 0;
      });
    }

    return filtered;
  }, [filter, rates, allCarriers]);

  const isTableLoading = useMemo(
    () => isCarriersLoading || (isRatesLoading && !rates.length),
    [isCarriersLoading, isRatesLoading, rates.length]
  );

  const content = (
    <>
      <StickyPageHeader isAdmin title="Rates" />
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <RatesFilter
          showAllCarriers
          carriers={allCarriers}
          filter={filter}
          modes={defaultModes}
          onFilterChange={setFilter}
        />
        <RateTable
          showAllCarriers
          allRatesForModals={rates}
          carriers={allCarriers}
          customEmptyText={!filter ? 'No filters selected' : false}
          highlightRateId={highlightRateId}
          loading={isTableLoading}
          rates={isTableLoading ? [] : filteredRates}
          selectedCarrier={null}
          shipTypes={allShipTypes}
          onClearFiltersAndGoToRate={handleClearFiltersAndGoToRate}
        />
      </Space>
    </>
  );

  if (disableLayout) return content;

  return (
    <Layout disableLoading showCarriersNavigation skipFullPageLoader menuIndex={0} routes={routes}>
      {content}
    </Layout>
  );
};

export default AdminRatesPage;
